'use client';

import { useIMQA } from '@/components/imqa-provider';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Slider } from '@/components/ui/slider';
import { useToast } from '@/hooks/use-toast';
import { useState, useEffect, useCallback } from 'react';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';

// Helper to read interaction options from localStorage config
function getInteractionOption(key: 'captureText' | 'captureId'): boolean {
  try {
    const saved = localStorage.getItem('imqaConfig');
    if (saved) {
      const parsed = JSON.parse(saved);
      const interactions = parsed?.instrumentations?.interactions;
      if (typeof interactions === 'object' && interactions !== null) {
        return interactions[key] !== false;
      }
    }
  } catch { /* ignore */ }
  return true;
}

// Helper to save interaction option to localStorage config
function setInteractionOption(key: 'captureText' | 'captureId', value: boolean): void {
  try {
    const saved = localStorage.getItem('imqaConfig');
    const parsed = saved ? JSON.parse(saved) : {};
    if (!parsed.instrumentations) parsed.instrumentations = {};
    const interactions = typeof parsed.instrumentations.interactions === 'object'
      ? parsed.instrumentations.interactions
      : {};
    interactions[key] = value;
    // CRITICAL: must include enabled: true, otherwise getPluginConfig
    // merges with defaults { enabled: false } and disables the instrumentation
    interactions.enabled = true;
    parsed.instrumentations.interactions = interactions;
    localStorage.setItem('imqaConfig', JSON.stringify(parsed));
  } catch { /* ignore */ }
}

export function FeatureToggles() {
  const { config, toggleFeature, isInitialized, autoReinit, setAutoReinit } = useIMQA();
  const { toast } = useToast();

  // Local state for interaction options (avoids reinit loop)
  const [captureText, setCaptureText] = useState(true);
  const [captureId, setCaptureId] = useState(true);

  useEffect(() => {
    setCaptureText(getInteractionOption('captureText'));
    setCaptureId(getInteractionOption('captureId'));
  }, []);

  const handleInteractionOptionToggle = useCallback((key: 'captureText' | 'captureId', checked: boolean) => {
    if (key === 'captureText') setCaptureText(checked);
    else setCaptureId(checked);
    setInteractionOption(key, checked);
    toast({
      title: `${key} ${checked ? 'enabled' : 'disabled'}`,
      description: 'Deactivate → Activate Agent 재초기화 시 적용됩니다.',
    });
  }, [toast]);

  const handleToggle = (feature: string, checked: boolean) => {
    if (!isInitialized) {
      toast({
        title: 'Agent not active',
        description: 'Please activate the IMQA Web Agent first',
        variant: 'destructive',
      });
      return;
    }

    toggleFeature(feature as keyof typeof config, checked);
    toast({
      title: `${feature} ${checked ? 'enabled' : 'disabled'}`,
      description: `The ${feature} feature has been ${
        checked ? 'enabled' : 'disabled'
      }`,
    });
  };
  const handleExporterProtocolChange = (value: string) => {
    if (!isInitialized) {
      toast({
        title: 'Agent not active',
        description: 'Please activate the IMQA Web Agent first',
        variant: 'destructive',
      });
      return;
    }
    toggleFeature('exporterProtocol', value);
    toast({
      title: 'Exporter protocol updated',
      description: `Exporter protocol set to ${value}`,
    });
  };

  const handleSamplingChange = (value: number[]) => {
    if (!isInitialized) {
      toast({
        title: 'Agent not active',
        description: 'Please activate the IMQA Web Agent first',
        variant: 'destructive',
      });
      return;
    }

    const samplingValue = value[0];
    toggleFeature('samplingProbability', samplingValue);
    toast({
      title: 'Sampling probability updated',
      description: `Sampling probability set to ${samplingValue}`,
    });
  };

  const handleAutoReinitToggle = (checked: boolean) => {
    setAutoReinit(checked);
    toast({
      title: `Auto Re-init ${checked ? 'enabled' : 'disabled'}`,
      description: checked
        ? 'IMQA will automatically re-initialize after deinit'
        : 'IMQA will NOT automatically re-initialize after deinit',
    });
  };

  return (
    <div className="space-y-6">
      {/* Auto Re-init setting - independent of initialization state */}
      <div className="space-y-3 pb-4 border-b">
        <div className="flex items-center justify-between">
          <Label htmlFor="autoReinit" className="flex flex-col space-y-1">
            <span>Auto Re-initialization</span>
            <span className="font-normal text-sm text-muted-foreground">
              Automatically reinitialize IMQA after deinit (e.g., due to dropPolicy)
            </span>
          </Label>
          <Switch
            id="autoReinit"
            checked={autoReinit}
            onCheckedChange={handleAutoReinitToggle}
          />
        </div>
      </div>

      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <Label htmlFor="consoleCapture" className="flex flex-col space-y-1">
            <span>Console Capture</span>
            <span className="font-normal text-sm text-muted-foreground">
              Capture console logs
            </span>
          </Label>
          <Switch
            id="consoleCapture"
            checked={config.consoleCapture}
            onCheckedChange={(checked) =>
              handleToggle('consoleCapture', checked)
            }
            disabled={!isInitialized}
          />
        </div>

        <div className="flex items-center justify-between">
          <Label
            htmlFor="advancedNetworkCapture"
            className="flex flex-col space-y-1"
          >
            <span>Advanced Network Capture</span>
            <span className="font-normal text-sm text-muted-foreground">
              Capture detailed network information
            </span>
          </Label>
          <Switch
            id="advancedNetworkCapture"
            checked={config.advancedNetworkCapture}
            onCheckedChange={(checked) =>
              handleToggle('advancedNetworkCapture', checked)
            }
            disabled={!isInitialized}
          />
        </div>

        <div className="flex items-center justify-between">
          <Label htmlFor="disableReplay" className="flex flex-col space-y-1">
            <span>Disable Replay</span>
            <span className="font-normal text-sm text-muted-foreground">
              Disable session replay functionality
            </span>
          </Label>
          <Switch
            id="disableReplay"
            checked={config.disableReplay}
            onCheckedChange={(checked) =>
              handleToggle('disableReplay', checked)
            }
            disabled={!isInitialized}
          />
        </div>

        <div className="flex items-center justify-between">
          <Label htmlFor="debug" className="flex flex-col space-y-1">
            <span>Debug Mode</span>
            <span className="font-normal text-sm text-muted-foreground">
              Enable debug logging
            </span>
          </Label>
          <Switch
            id="debug"
            checked={config.debug}
            onCheckedChange={(checked) => handleToggle('debug', checked)}
            disabled={!isInitialized}
          />
        </div>

        <div className="flex items-center justify-between">
          <Label htmlFor="metric" className="flex flex-col space-y-1">
            <span>Capture Metrics</span>
            <span className="font-normal text-sm text-muted-foreground">
              Enable Metric
            </span>
          </Label>
          <Switch
            id="metric"
            checked={config.captureMetrics}
            onCheckedChange={(checked) =>
              handleToggle('captureMetrics', checked)
            }
            disabled={!isInitialized}
          />
        </div>
        <div className="flex items-center justify-between">
          <Label htmlFor="disablePreflight" className="flex flex-col space-y-1">
            <span>Disable Preflight</span>
            <span className="font-normal text-sm text-muted-foreground">
              Disable preflight checks
            </span>
          </Label>
          <Switch
            id="disablePreflight"
            checked={config.disablePreflight}
            onCheckedChange={(checked) =>
              handleToggle('disablePreflight', checked)
            }
            disabled={!isInitialized}
          />
        </div>
        <div className="flex items-center justify-between">
          <Label htmlFor="exporterProtocol" className="flex flex-col space-y-1">
            <span>Exporter Protocol</span>
            <span className="font-normal text-sm text-muted-foreground">
              Set exporter protocol
            </span>
          </Label>
          <div className="w-24">
            <Select
              value={config.exporterProtocol}
              onValueChange={(value) => handleExporterProtocolChange(value)}
              disabled={!isInitialized}
            >
              <SelectTrigger>
                <SelectValue placeholder="Select exporter protocol" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="json">JSON</SelectItem>
                <SelectItem value="proto">Proto</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
        
        <div className="flex items-center justify-between">
          <Label htmlFor="sessionStorage" className="flex flex-col space-y-1">
            <span>Session Storage</span>
            <span className="font-normal text-sm text-muted-foreground">
              Where to store session ID
            </span>
          </Label>
          <div className="w-32">
            <Select
              value={config.sessionStorage || 'cookie'}
              onValueChange={(value) => handleToggle('sessionStorage', value as any)}
              disabled={!isInitialized}
            >
              <SelectTrigger>
                <SelectValue placeholder="Select storage" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="cookie">Cookie</SelectItem>
                <SelectItem value="localStorage">LocalStorage</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
      </div>

      <div className="space-y-3 pb-4 border-b">
        <h4 className="text-sm font-medium">User Interaction Options</h4>
        <p className="text-xs text-muted-foreground">
          변경 후 Deactivate → Activate Agent로 재초기화해야 적용됩니다.
        </p>
        <div className="flex items-center justify-between">
          <Label htmlFor="captureText" className="flex flex-col space-y-1">
            <span>Capture Text</span>
            <span className="font-normal text-sm text-muted-foreground">
              Capture innerText from interaction elements
            </span>
          </Label>
          <Switch
            id="captureText"
            checked={captureText}
            onCheckedChange={(checked) => handleInteractionOptionToggle('captureText', checked)}
          />
        </div>

        <div className="flex items-center justify-between">
          <Label htmlFor="captureId" className="flex flex-col space-y-1">
            <span>Capture ID</span>
            <span className="font-normal text-sm text-muted-foreground">
              Capture element ID from interaction elements
            </span>
          </Label>
          <Switch
            id="captureId"
            checked={captureId}
            onCheckedChange={(checked) => handleInteractionOptionToggle('captureId', checked)}
          />
        </div>
      </div>

      <div className="space-y-3">
        <Label
          htmlFor="samplingProbability"
          className="flex flex-col space-y-1"
        >
          <span>Sampling Probability: {config.samplingProbability}</span>
          <span className="font-normal text-sm text-muted-foreground">
            Set sampling probability (0 to 1)
          </span>
        </Label>
        <Slider
          id="samplingProbability"
          min={0}
          max={1}
          step={0.1}
          value={[Number(config.samplingProbability) || 1]}
          onValueChange={handleSamplingChange}
          disabled={!isInitialized}
        />
      </div>
    </div>
  );
}
